import type {
  APIConnector,
  AutocompleteQueryConfig,
  AutocompleteResponseState,
  QueryConfig,
  RequestState,
  ResponseState,
} from '@elastic/search-ui'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import {
  SearchResponseInterface,
  SearchResultInterface,
} from 'common/types/search-interfaces'
import { getWebsiteSearch } from 'src/publisher/api/website-search-api'
import { toResponseState } from 'src/shared/entities/search/utils'
import { v4 as uuid } from 'uuid'

interface AutocompleteResultClickParams {
  path: string
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) {
    return error.message
  }

  return String(error || fallback)
}

export class WebsiteSearchAPIConnector implements APIConnector {
  private readonly locale: PageLocaleEnum

  private readonly limit: number

  constructor(locale: PageLocaleEnum, limit = 3) {
    this.locale = locale
    this.limit = limit
  }

  async onSearch(
    state: RequestState,
    queryConfig: QueryConfig,
  ): Promise<ResponseState> {
    const { searchTerm = '', current } = state
    const resultsPerPage =
      (queryConfig as QueryConfig)?.resultsPerPage ??
      Number((state as RequestState)?.resultsPerPage) ??
      this.limit

    try {
      const response: SearchResponseInterface<SearchResultInterface> =
        await getWebsiteSearch({
          query: searchTerm,
          limit: resultsPerPage.toString(),
          page: current?.toString(),
          locale: this.locale,
        })

      return toResponseState(response, {
        searchTerm,
        currentPage: current ?? 1,
        resultsPerPage,
      })
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Search request failed'))
    }
  }

  async onAutocomplete(
    state: RequestState,
    _queryConfig: AutocompleteQueryConfig,
  ): Promise<AutocompleteResponseState> {
    const { searchTerm = '' } = state

    try {
      const response: SearchResponseInterface<SearchResultInterface> =
        await getWebsiteSearch({
          query: searchTerm,
          limit: this.limit.toString(),
          locale: this.locale,
        })

      return {
        autocompletedResults: [
          {
            data: response.data,
            total: response.total,
          },
        ],
        autocompletedResultsRequestId: uuid(),
        autocompletedSuggestions: {},
        autocompletedSuggestionsRequestId: uuid(),
      }
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Autocomplete request failed'))
    }
  }

  onResultClick(): void {
    //
  }

  onAutocompleteResultClick(params: AutocompleteResultClickParams): void {
    window.location.href = `${window.location.origin}${params.path}`
  }
}
